import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'app-radio-button-input',
  standalone: true,
  templateUrl: './radio-button-input.component.html',
  styleUrls: ['./radio-button-input.component.scss'],
  imports: [CommonModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RadioButtonInputComponent),
      multi: true
    }
  ],
  changeDetection: ChangeDetectionStrategy.Default
})
export class RadioButtonInputComponent implements ControlValueAccessor, AfterViewInit {
  @ViewChild('inputEl') private inputEl?: ElementRef<HTMLInputElement>;

  @Input() controlId: string | null = null;
  @Input() value: any;
  @Input() name: string | null = null;
  @Output() selectionChange = new EventEmitter<any>();

  // A radio group shares a single FormControl, so an individual option can only be turned off
  // through an input. The control can independently disable every option in the group.
  @Input() set optionDisabled(optionDisabled: boolean) {
    this._optionDisabled = optionDisabled;
    this.updateDisabled();
  }

  public generatedRandomId = `radio-${Math.random().toString(36).slice(2)}`;

  public disabled = false;
  private _optionDisabled = false;
  private controlDisabled = false;
  private modelValue: any;

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  ngAfterViewInit(): void {
    if (this.inputEl) {
      this.inputEl.nativeElement.checked = this.value === this.modelValue;
    }
  }

  writeValue(value: any): void {
    this.modelValue = value;
    if (this.inputEl) {
      this.inputEl.nativeElement.checked = this.value === this.modelValue;
    }
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.controlDisabled = isDisabled;
    this.updateDisabled();
  }

  private updateDisabled(): void {
    this.disabled = this._optionDisabled || this.controlDisabled;
  }

  onInputChange(): void {
    if (!this.disabled) {
      this.onChange(this.value);
      this.onTouched();
      this.selectionChange.emit(this.value);
    }
  }
}
